import { useCallback, useEffect, useRef, useState } from 'react';
import apiService from '../services/api';
import { BoxscoreResponse } from '../types';

const POLL_MS = 30000;

export function useMatchupBoxscore(matchupId: number) {
  const [data, setData] = useState<BoxscoreResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<number | null>(null);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchOnce = useCallback(async () => {
    try {
      const res = await apiService.getBoxscore(matchupId);
      setData(res);
      setError(null);
      setLastUpdatedAt(Date.now());
      return res;
    } catch (e) {
      setError('Could not load the scoreboard.');
      return null;
    } finally {
      setLoading(false);
    }
  }, [matchupId]);

  useEffect(() => {
    let cancelled = false;
    const clear = () => { if (timer.current) { clearInterval(timer.current); timer.current = null; } };
    fetchOnce().then(res => {
      if (cancelled) return;
      if (res && res.is_completed === false) {
        timer.current = setInterval(async () => {
          if (document.hidden) return;
          const next = await fetchOnce();
          if (next && next.is_completed) clear();
        }, POLL_MS);
      }
    });
    return () => { cancelled = true; clear(); };
  }, [fetchOnce]);

  return { data, loading, error, lastUpdatedAt, refresh: fetchOnce };
}
